import React, { useState } from 'react';

// 定义汉字训练数据结构
interface CharacterTrainingData {
  id: string;
  character: string;
  pinyin: string;
  meaning: string;
  strokeOrder: string[];
}

const TrainingPage: React.FC = () => {
  // Mock汉字训练数据
  const [trainingCharacters, setTrainingCharacters] = useState<CharacterTrainingData[]>([
    {
      id: 'char1',
      character: '人',
      pinyin: 'rén',
      meaning: 'person',
      strokeOrder: ['left-falling', 'right-falling']
    },
    {
      id: 'char2',
      character: '好',
      pinyin: 'hǎo',
      meaning: 'good',
      strokeOrder: ['dot-left-falling', 'left-falling', 'horizontal', 'horizontal-left-falling', 'vertical-hook', 'horizontal']
    },
    {
      id: 'char3',
      character: '学',
      pinyin: 'xué',
      meaning: 'learn',
      strokeOrder: ['dot', 'dot', 'left-falling', 'dot', 'horizontal-left-falling', 'horizontal-left-falling', 'vertical-hook', 'horizontal']
    },
    {
      id: 'char4',
      character: '习',
      pinyin: 'xí',
      meaning: 'study',
      strokeOrder: ['horizontal-folding-hook', 'dot', 'rising', 'horizontal-folding-hook', 'left-falling']
    },
    {
      id: 'char5',
      character: '中',
      pinyin: 'zhōng',
      meaning: 'middle',
      strokeOrder: ['vertical', 'horizontal-folding', 'horizontal', 'vertical', 'horizontal']
    }
  ]);

  // 当前训练的汉字索引
  const [currentIndex, setCurrentIndex] = useState(0);
  // 反馈信息
  const [feedback, setFeedback] = useState<string | null>(null);
  // 反馈类型：'correct'或'error'
  const [feedbackType, setFeedbackType] = useState<'correct' | 'error' | null>(null);

  // 获取当前训练的汉字
  const currentCharacter = trainingCharacters[currentIndex];

  // 继续下一个汉字
  const nextCharacter = () => {
    if (currentIndex < trainingCharacters.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setFeedback(null);
      setFeedbackType(null);
    } else {
      setFeedback('Congratulations! You have completed all training!');
      setFeedbackType('correct');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Character Training</h1>
          <p className="text-lg text-gray-600 mt-2">Learn Chinese characters and pinyin</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 max-w-3xl mx-auto">
          {/* 汉字显示区域 */}
          <div className="text-center mb-8">
            <div className="inline-block p-8 bg-primary/10 rounded-full mb-4">
              <span className="text-9xl font-bold text-primary">{currentCharacter.character}</span>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <p className="text-2xl text-gray-700">{currentCharacter.pinyin}</p>
                <button
                  onClick={() => {
                    // 模拟播放拼音读音
                    const utterance = new SpeechSynthesisUtterance(currentCharacter.pinyin);
                    utterance.lang = 'zh-CN';
                    speechSynthesis.speak(utterance);
                  }}
                  className="text-primary hover:text-primary/80 focus:outline-none"
                  aria-label="Play pinyin pronunciation"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
              <p className="text-2xl text-gray-700">{currentCharacter.meaning}</p>
            </div>
          </div>

          {/* 反馈信息 */}
          {feedback && (
            <div className={`p-3 mb-6 rounded-md ${feedbackType === 'correct' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {feedback}
            </div>
          )}

          {/* 笔画顺序 */}
          <div className="mb-6 max-w-md mx-auto">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Stroke Order:</h3>
            <div className="flex flex-wrap gap-2 justify-center">
              {currentCharacter.strokeOrder.map((stroke, index) => (
                <span key={index} className="px-2 py-1 bg-secondary/10 text-secondary rounded">
                  {stroke}
                </span>
              ))}
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex justify-between max-w-md mx-auto">
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Back
            </button>
            <button
              onClick={nextCharacter}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              {currentIndex < trainingCharacters.length - 1 ? 'Next' : 'Restart'}
            </button>
          </div>
        </div>

        {/* 进度指示器 */}
        <div className="mt-8">
          <div className="text-center text-sm text-gray-600 mb-2">
            Progress: {currentIndex + 1}/{trainingCharacters.length}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${((currentIndex + 1) / trainingCharacters.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingPage;